import { events } from '../analytics/events';
import { extractFacetsWithCountedValues } from '../compute/widget-state';
import {
  FacetName,
  facetNames,
  Order,
  PaperFilter,
} from '../models/widget-state';
import { MultiSelectList } from './MultiSelectList';
import { Paper } from '../models/result-set';
import { Scroller } from '../styled/mui';
import { SortComponent } from './SortComponent';
import { useTrackedHandler } from '../hooks/useTracking';

import { Button } from '@mui/material';
import { useState } from 'react';
import capitalize from 'lodash-es/capitalize';
import styled from 'styled-components';

interface FacetsPanelProps {
  papers: Array<Paper>;
  filters: Array<PaperFilter>;
  onSelectedFilter?: (facetName: FacetName, value: string) => void;
  onUnSelectedFilter?: (facetName: FacetName, value: string) => void;
  maxOpenSubPanels?: number;
  ordering: Order | null;
  onOrderChanged?: (order: Order | null) => void;
  onClearAllFiltersClicked?: () => void;
}

const sortPanelKey = 'sort' as const;
const panelNames = [sortPanelKey, ...facetNames];
type PanelName = (typeof panelNames)[number];

export function FacetsPanel(props: FacetsPanelProps): JSX.Element {
  const {
    papers,
    filters,
    onSelectedFilter = () => {},
    onUnSelectedFilter = () => {},
    maxOpenSubPanels = 2,
    ordering,
    onOrderChanged = () => {},
    onClearAllFiltersClicked = () => {},
  } = props;

  const hasFilters = filters.length > 0;
  const facets = extractFacetsWithCountedValues(papers, filters);
  const relevanceFacet = facets.find((f) => f.facetName === 'relevance');
  const numRelevanceValues = relevanceFacet?.values?.length;
  const hasRelevanceValues = numRelevanceValues && numRelevanceValues > 0;

  const selectedItemsPerFacet = computeSelectedFacets(filters);
  const [expandedPanels, setExpandedPanels] = useState<Array<PanelName>>([
    hasRelevanceValues ? 'relevance' : 'year',
    sortPanelKey,
  ]);

  const handleExpandedChange = useTrackedHandler(
    events.panels.ShowFacetChanged,
    (panelName: PanelName, isExpanded: boolean): void => {
      setExpandedPanels((prev) => {
        if (isExpanded) {
          return [panelName, ...prev].slice(0, maxOpenSubPanels);
        } else {
          return prev.filter((v) => v !== panelName);
        }
      });
    },
    (panelName, isExpanded) => ({ panelName, isExpanded }),
  );

  return (
    <MainLayout>
      <SortPanel
        key={sortPanelKey}
        ordering={ordering}
        onOrderChanged={onOrderChanged}
        isExpanded={expandedPanels.includes(sortPanelKey)}
        onExpandedChange={(expanded) =>
          handleExpandedChange(sortPanelKey, expanded)
        }
      />
      <div>
        {facets.map(
          (facet) =>
            facet.values.length > 0 && (
              <MultiSelectList
                key={facet.facetName}
                items={facet.values}
                label={`${capitalize(facet.facetName)}`}
                selectedItems={selectedItemsPerFacet[facet.facetName]}
                onSelected={(value: string) =>
                  onSelectedFilter(facet.facetName, value)
                }
                onUnselected={(value: string) =>
                  onUnSelectedFilter(facet.facetName, value)
                }
                isExpanded={expandedPanels.includes(facet.facetName)}
                onExpandedChange={(expanded) =>
                  handleExpandedChange(facet.facetName, expanded)
                }
              />
            ),
        )}
      </div>
      {hasFilters && (
        <ActionsDiv>
          <TextActionButton onClick={onClearAllFiltersClicked}>
            Clear Filters
          </TextActionButton>
        </ActionsDiv>
      )}
    </MainLayout>
  );
}

function computeSelectedFacets(
  filters: Array<PaperFilter>,
): Record<FacetName, Array<string>> {
  const result = {
    year: [],
    venue: [],
    author: [],
    relevance: [],
    criteria: [],
  } as Record<FacetName, Array<string>>;
  for (const filter of filters) {
    result[filter.categoryName].push(filter.value);
  }

  return result;
}

const SortPanel = styled(SortComponent)`
  margin-bottom: 8px;
`;

const ActionsDiv = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
`;

const TextActionButton = styled(Button)`
  &.MuiButtonBase-root.MuiButton-root {
    padding: 4px;
    margin: 2px 16px 0px 16px;
    font-weight: 400;
  }
`;

const MainLayout = styled(Scroller)`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  width: 280px;
  margin: 20px 20px 0px 20px;
  overflow: hidden;
  gap: 10px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: auto;
  }
`;
